'use client';

import React, { useEffect, useMemo, useRef } from 'react';

import { Section, Container } from '@layout';
import {
  Button,
  Card,
  Heading,
  SearchSuggest,
  searchSuggestBus,
  type SearchSuggestHandle,
  type SearchSuggestItem,
} from '@ui';
import { IconSearch } from '@icons';
import MarkupSnippet, { markupCopyLabels } from '../markup-snippet/MarkupSnippet';
import { getDesignSystemNavItems } from '@components/design-system';

interface SearchSuggestDemoProps {
  sectionID?: string;
  classes?: string;
  containerId?: string;
  containerClasses?: string;
  isSpanish?: boolean;
}

const SearchSuggestDemo: React.FC<SearchSuggestDemoProps> = ({
  isSpanish = false,
  sectionID,
  classes = '',
  containerId,
  containerClasses,
}) => {
  const searchRef = useRef<SearchSuggestHandle>(null);
  const t = (en: string, es: string) => (isSpanish ? es : en);

  const openSearch = () => searchRef.current?.openSearch();

  const suggestions = useMemo<SearchSuggestItem[]>(
    () => getDesignSystemNavItems({ isSpanish, selfHref: '#search-suggest' }),
    [isSpanish]
  );

  const handleSelect = (item: SearchSuggestItem) => {
    if (!item.meta || !item.meta.startsWith('#')) return;
    const target = document.querySelector(item.meta);
    if (target) target.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(
    () =>
      searchSuggestBus.subscribe((action) => {
        if (action === 'open') searchRef.current?.openSearch();
        else if (action === 'close') searchRef.current?.closeSearch();
        else if (action === 'toggle') searchRef.current?.toggleSearch();
      }),
    []
  );

  return (
    <Section className={classes} {...(sectionID ? { id: sectionID } : {})}>
      <Container containerId={containerId} containerClasses={containerClasses}>
        <Heading as='h3'>{t('Search Suggest', 'Búsqueda con sugerencias')}</Heading>
        <p className='sub full'>
          {t(
            'Header-anchored search field with a filtered suggestion card. Sits below the header and above page content so header links stay clickable. Wire any opener — typically a ',
            'Campo de búsqueda anclado al encabezado con una tarjeta de sugerencias filtradas. Se ubica debajo del encabezado y sobre el contenido para que los enlaces del encabezado sigan clicables. Conecta cualquier disparador — normalmente un '
          )}
          <code>SearchToggle</code>
          {t(
            ' in your header — to its imperative handle (or to ',
            ' en tu encabezado — a su `ref` imperativo (o a '
          )}
          <code>searchSuggestBus</code>
          {t(
            ' for cross-tree openers). ',
            ' para disparadores en árboles distintos). '
          )}
          {t(
            'Click the button below, then start typing — use',
            'Haz clic en el botón siguiente y empieza a escribir — usa'
          )}{' '}
          <kbd>↑</kbd>/<kbd>↓</kbd>{' '}
          {t(
            'to navigate, Enter to pick, and Esc or the × to close.',
            'para navegar, Enter para elegir y Esc o la × para cerrar.'
          )}
        </p>
        <Card>
          <div className='row btn-row'>
            <Button
              variant='primary'
              icon={<IconSearch />}
              label={t('Search', 'Buscar')}
              onClick={openSearch}
              aria-label={t('Open search', 'Abrir búsqueda')}
            />
          </div>
        </Card>
      <MarkupSnippet
          {...markupCopyLabels(isSpanish)}
          code={`<label for="site-search">Search</label>
<input id="site-search" type="search" />
<ul class="suggest-list" role="listbox">
  <li role="option">Buttons</li>
</ul>`}
        />
      </Container>

      <SearchSuggest
        ref={searchRef}
        id='search-suggest-demo'
        suggestions={suggestions}
        onSelect={handleSelect}
        placeholder={t(
          'Search design system sections…',
          'Busca secciones del sistema de diseño…'
        )}
        ariaLabel={t('Search design system sections', 'Buscar secciones del sistema de diseño')}
        suggestionsLabel={t('Suggestions', 'Sugerencias')}
        closeAriaLabel={t('Close search', 'Cerrar búsqueda')}
      />
    </Section>
  );
};

export default SearchSuggestDemo;
